import { useSyncExternalStore } from "react";
import {
  getAnalyticsConsent,
  setAnalyticsConsent,
  subscribeAnalyticsConsent,
} from "@repo/shared/analyticsConsent";

import { Button } from "~/components/ui/button";

function AnalyticsChoiceActions({ size = "default" }: { size?: "default" | "sm" }) {
  return (
    <div className={size === "sm" ? "mt-3 flex justify-center gap-2" : "mt-4 flex justify-end gap-2"}>
      <Button size={size} variant="outline" onClick={() => setAnalyticsConsent(false)}>Reject</Button>
      <Button size={size} onClick={() => setAnalyticsConsent(true)}>Accept</Button>
    </div>
  );
}

/** Consent withdrawal must remain available without a signed-in session. */
export function SignedOutAnalyticsPreferences() {
  const consent = useSyncExternalStore(subscribeAnalyticsConsent, getAnalyticsConsent, getAnalyticsConsent);
  return (
    // A quiet line under the sign-in card, closed by default: the choice
    // stays reachable without a session, without a box asking for attention.
    <details className="group/privacy mt-6 text-center text-xs">
      <summary className="inline cursor-pointer list-none text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-ring [&::-webkit-details-marker]:hidden">
        Privacy choices
      </summary>
      <p className="mx-auto mt-2 max-w-xs text-muted-foreground" aria-live="polite">
        Optional product analytics and masked session recordings are {consent === "accepted" ? "on" : "off"}.
        Sign-in works with either choice.
      </p>
      <AnalyticsChoiceActions size="sm" />
    </details>
  );
}

/**
 * First-run analytics prompt. It only appears while no answer is stored;
 * Later changes are available in Settings → Security → Product analytics
 * or through Privacy choices on the sign-in page.
 */
export function AnalyticsConsent() {
  const consent = useSyncExternalStore(
    subscribeAnalyticsConsent,
    getAnalyticsConsent,
    getAnalyticsConsent,
  );
  if (consent !== "unknown") return null;
  return (
    <section
      aria-label="Analytics privacy choices"
      className="dialog-glass fixed right-3 bottom-3 left-3 z-[100] max-w-md rounded-2xl border p-5 text-foreground shadow-lg sm:left-auto"
    >
      <p className="font-medium text-sm">Choose whether to share analytics</p>
      <p className="mt-1.5 text-muted-foreground text-sm leading-relaxed">
        Optional product analytics and masked session recordings help us understand product use.
        Essential sign-in and security features work with either choice. You can change this any
        time in Settings or Privacy choices on the sign-in page.{" "}
        <a
          className="text-foreground underline underline-offset-4 transition-colors hover:text-primary"
          href="https://www.amoreg.com/privacy-policy"
          target="_blank"
          rel="noreferrer"
        >
          Privacy policy
        </a>
      </p>
      <AnalyticsChoiceActions />
    </section>
  );
}
